/**
 * Build fund–investor × quarter document coverage from portfolio API shapes.
 * CAS activity uses amounts_called / distributions_period with a small epsilon.
 */

/** Ignore near-zero CAS line items when inferring required DD/dist rows. */
export const CAS_AMOUNT_EPSILON = 0.005

const Q_LABEL = ['Q1', 'Q2', 'Q3', 'Q4']

export function dateToQuarter(isoDate) {
  if (!isoDate || typeof isoDate !== 'string') return null
  const d = new Date(isoDate.slice(0, 10) + 'T12:00:00Z')
  if (Number.isNaN(d.getTime())) return null
  const m = d.getUTCMonth()
  const y = d.getUTCFullYear()
  const q = Math.floor(m / 3) + 1
  return { year: y, q }
}

export function quarterKey(yq) {
  return `${yq.year}-${yq.q}`
}

export function quarterLabel(yq) {
  return `${Q_LABEL[yq.q - 1]} ${yq.year}`
}

/** Order: negative if a < b, 0 if equal, positive if a > b */
export function compareQuarter(a, b) {
  return a.year * 4 + a.q - (b.year * 4 + b.q)
}

export function minQuarter(a, b) {
  return compareQuarter(a, b) <= 0 ? a : b
}

export function maxQuarter(a, b) {
  return compareQuarter(a, b) >= 0 ? a : b
}

export function nextQuarter(yq) {
  if (yq.q === 4) return { year: yq.year + 1, q: 1 }
  return { year: yq.year, q: yq.q + 1 }
}

export function currentQuarterUtc() {
  const now = new Date()
  const m = now.getUTCMonth()
  const y = now.getUTCFullYear()
  return { year: y, q: Math.floor(m / 3) + 1 }
}

function num(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function hasActivity(v) {
  const n = num(v)
  return n != null && Math.abs(n) > CAS_AMOUNT_EPSILON
}

function yearToQ1(year) {
  if (year == null || !Number.isFinite(Number(year))) return null
  const y = parseInt(year, 10)
  if (!Number.isFinite(y)) return null
  return { year: y, q: 1 }
}

function iterQuarters(from, to) {
  const out = []
  let cur = from
  while (compareQuarter(cur, to) <= 0) {
    out.push({ ...cur })
    cur = nextQuarter(cur)
  }
  return out
}

function fundInvestorKey(fund, investor) {
  return `${fund}||${investor}`
}

/**
 * @param {object} params
 * @param {Array} params.funds - API funds rows
 * @param {Array} params.cas_timeline
 * @param {Array} params.drawdowns
 * @param {Array} params.distributions
 * @param {Record<string, object>|null} params.fundVariables - vars map from usePortfolioData
 */
export function buildCoverageMatrix({
  funds,
  cas_timeline = [],
  drawdowns = [],
  distributions = [],
  fundVariables = null,
}) {
  const casByKey = new Map()
  const ddByKey = new Map()
  const distByKey = new Map()

  for (const c of cas_timeline) {
    const k = fundInvestorKey(c.fund, c.investor)
    const yq = dateToQuarter(c.date)
    if (!yq) continue
    if (!casByKey.has(k)) casByKey.set(k, new Map())
    const qk = quarterKey(yq)
    const bucket = casByKey.get(k).get(qk) || []
    bucket.push(c)
    casByKey.get(k).set(qk, bucket)
  }

  for (const d of drawdowns) {
    const k = fundInvestorKey(d.fund, d.investor)
    const yq = dateToQuarter(d.date)
    if (!yq) continue
    if (!ddByKey.has(k)) ddByKey.set(k, new Set())
    ddByKey.get(k).add(quarterKey(yq))
  }

  for (const d of distributions) {
    const k = fundInvestorKey(d.fund, d.investor)
    const yq = dateToQuarter(d.date)
    if (!yq) continue
    if (!distByKey.has(k)) distByKey.set(k, new Set())
    distByKey.get(k).add(quarterKey(yq))
  }

  const perRowPrelim = []
  const nowQ = currentQuarterUtc()

  for (const f of funds || []) {
    const k = fundInvestorKey(f.fund, f.investor)
    const fv = fundVariables?.[k]
    const firstInv = fv?.['First Investment Year']
    const vintage = fv?.['Vintage Year']
    let prelim =
      yearToQ1(firstInv) ||
      yearToQ1(vintage) ||
      null

    const casMap = casByKey.get(k)
    if (!prelim && casMap && casMap.size > 0) {
      let earliest = null
      for (const yqStr of casMap.keys()) {
        const [ys, qs] = yqStr.split('-')
        const yq = { year: parseInt(ys, 10), q: parseInt(qs, 10) }
        if (!earliest || compareQuarter(yq, earliest) < 0) earliest = yq
      }
      prelim = earliest
    }

    perRowPrelim.push({ key: k, fund: f.fund, investor: f.investor, prelim })
  }

  let gridStart = null
  for (const { prelim } of perRowPrelim) {
    if (prelim) gridStart = gridStart ? minQuarter(gridStart, prelim) : prelim
  }

  if (!gridStart) {
    for (const c of cas_timeline) {
      const yq = dateToQuarter(c.date)
      if (!yq) continue
      gridStart = gridStart ? minQuarter(gridStart, yq) : yq
    }
  }
  if (!gridStart) gridStart = nowQ

  const quarters = iterQuarters(gridStart, nowQ)

  const rows = perRowPrelim.map(({ key, fund, investor, prelim }) => {
    const effectiveStart = prelim || gridStart
    const casMap = casByKey.get(key) || new Map()
    const ddSet = ddByKey.get(key) || new Set()
    const distSet = distByKey.get(key) || new Set()

    const cells = quarters.map((yq) => {
      const qk = quarterKey(yq)
      const beforeStart = compareQuarter(yq, effectiveStart) < 0

      if (beforeStart) {
        return {
          quarter: yq,
          quarterKey: qk,
          status: 'na',
          casOk: null,
          drawdownOk: null,
          distOk: null,
          needsDrawdownDetail: false,
          needsDistDetail: false,
        }
      }

      const casRows = casMap.get(qk) || []
      const hasCas = casRows.length > 0
      let callActivity = false
      let distActivity = false
      for (const row of casRows) {
        if (hasActivity(row.amounts_called)) callActivity = true
        if (hasActivity(row.distributions_period)) distActivity = true
      }

      const drawdownOk =
        !hasCas ? null : !callActivity ? null : ddSet.has(qk)
      const distOk =
        !hasCas ? null : !distActivity ? null : distSet.has(qk)

      return {
        quarter: yq,
        quarterKey: qk,
        status: hasCas ? 'cas_ok' : 'cas_missing',
        casOk: hasCas,
        drawdownOk,
        distOk,
        needsDrawdownDetail: hasCas && callActivity && !ddSet.has(qk),
        needsDistDetail: hasCas && distActivity && !distSet.has(qk),
      }
    })

    return {
      key,
      fund,
      investor,
      prelimStart: prelim,
      effectiveStart,
      cells,
    }
  })

  return { quarters, rows, gridStart, nowQuarter: nowQ }
}
